/**
 * Role-aware left slide-in menu, available everywhere — including the
 * full-screen map home — so mobile users can reach their console without
 * leaving the map first.
 */

import { useState } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { LogIn, LogOut, Menu, UserPlus } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { AppLogo } from "./AppLogo";
import { useAuth } from "@/lib/auth";
import { ADMIN_NAV, CUSTOMER_NAV, PARTNER_NAV, SUPER_ADMIN_NAV } from "@/lib/nav";
import type { NavItem } from "./DashboardShell";

const GUEST_NAV: NavItem[] = CUSTOMER_NAV.filter((i) => i.to === "/compare");

/** Menu items + label for the highest role the signed-in user holds. */
function menuForRoles(hasRole: (r: "customer" | "partner" | "admin" | "super_admin") => boolean): {
  label: string;
  items: NavItem[];
} {
  if (hasRole("super_admin")) return { label: "Super Admin", items: SUPER_ADMIN_NAV };
  if (hasRole("admin")) return { label: "Admin", items: ADMIN_NAV };
  if (hasRole("partner")) return { label: "Partner", items: PARTNER_NAV };
  if (hasRole("customer")) return { label: "Customer", items: CUSTOMER_NAV };
  return { label: "Guest", items: GUEST_NAV };
}

export function AppMenuDrawer({ className = "" }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const { user, profile, hasRole, signOut } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const nav = useNavigate();
  const { label, items } = menuForRoles(hasRole);

  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
    void nav({ to: "/login" });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger
        aria-label="Open menu"
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary/70 text-brown-dark transition hover:bg-brand-soft ${className}`}
      >
        <Menu className="h-4.5 w-4.5" />
      </SheetTrigger>
      <SheetContent side="left" className="flex w-[85vw] flex-col p-0 sm:max-w-xs">
        <SheetTitle className="sr-only">Landoo menu</SheetTitle>
        <SheetDescription className="sr-only">
          Navigate to your dashboard, listings, enquiries and account pages.
        </SheetDescription>

        {/* Fixed header */}
        <div className="shrink-0 border-b border-border px-4 py-3">
          <AppLogo />
          <div className="mt-2 inline-block rounded-lg bg-brand-soft px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-brown-dark">
            {label}
          </div>
        </div>

        {/* Scrollable menu body */}
        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain p-3">
          {items.map(({ to, label: itemLabel, icon: Icon }) => {
            const active = pathname === to;
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
                  active
                    ? "bg-brown text-primary-foreground shadow-soft"
                    : "text-brown-dark hover:bg-brand-soft"
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{itemLabel}</span>
              </Link>
            );
          })}
        </nav>

        {/* Fixed footer */}
        <div
          className="shrink-0 border-t border-border p-3"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        >
          {user ? (
            <>
              <div className="mb-2 min-w-0 px-2 text-xs text-muted-foreground">
                <div className="truncate font-semibold text-brown-dark">
                  {profile?.full_name ?? "User"}
                </div>
                <div className="truncate">{profile?.email ?? ""}</div>
              </div>
              <button
                type="button"
                onClick={handleSignOut}
                className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium text-brown-dark hover:bg-brand-soft"
              >
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </>
          ) : (
            <div className="flex gap-2">
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-border px-3 py-2.5 text-sm font-semibold text-brown-dark"
              >
                <LogIn className="h-4 w-4" /> Sign in
              </Link>
              <Link
                to="/register"
                onClick={() => setOpen(false)}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-brown px-3 py-2.5 text-sm font-semibold text-primary-foreground"
              >
                <UserPlus className="h-4 w-4" /> Register
              </Link>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
